import React, { useEffect, useState, useCallback } from 'react';
import { performanceMonitor, PerformanceMetrics, ComponentPerformanceData } from '../../utils/performance/monitoring';

interface PerformanceMonitorProps {
  enabled?: boolean;
  showDevTools?: boolean;
  threshold?: number;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({
  enabled = process.env.NODE_ENV === 'development',
  showDevTools = false,
  threshold = 16
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [componentMetrics, setComponentMetrics] = useState<ComponentPerformanceData[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const updateMetrics = useCallback(() => {
    if (enabled) {
      setMetrics(performanceMonitor.getMetrics());
      setComponentMetrics(performanceMonitor.getComponentMetrics());
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { return; }

    const interval = setInterval(updateMetrics, 2000);
    updateMetrics();

    return () => {
      clearInterval(interval);
    };
  }, [enabled, updateMetrics]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        setIsVisible(prev => !prev);
      }
    };

    if (showDevTools) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [showDevTools]);

  if (!enabled || (!showDevTools && !isVisible)) {
    return null;
  }

  const slowComponents = componentMetrics.filter(comp => comp.renderTime > threshold);

  const getMetricStatus = (value: number | null, thresholds: { good: number; poor: number }) => {
    if (value === null) { return 'unknown'; }
    if (value <= thresholds.good) { return 'good'; }
    if (value <= thresholds.poor) { return 'needs-improvement'; }
    return 'poor';
  };

  const formatMetricValue = (value: number | null, unit: string = 'ms') => {
    return value !== null ? `${value.toFixed(2)}${unit}` : 'N/A';
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-md z-50 text-sm">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">Performance Monitor</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>

      <div className="space-y-2">
        <h4 className="font-medium text-gray-700">Core Web Vitals</h4>
        
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className={`p-2 rounded ${
            getMetricStatus(metrics?.lcp || null, { good: 2500, poor: 4000 }) === 'good' 
              ? 'bg-green-100 text-green-800' 
              : getMetricStatus(metrics?.lcp || null, { good: 2500, poor: 4000 }) === 'needs-improvement'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-medium">LCP</div>
            <div>{formatMetricValue(metrics?.lcp || null)}</div>
          </div>

          <div className={`p-2 rounded ${
            getMetricStatus(metrics?.ttfb || null, { good: 800, poor: 1800 }) === 'good' 
              ? 'bg-green-100 text-green-800' 
              : getMetricStatus(metrics?.ttfb || null, { good: 800, poor: 1800 }) === 'needs-improvement'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-medium">TTFB</div>
            <div>{formatMetricValue(metrics?.ttfb || null)}</div>
          </div>

          <div className={`p-2 rounded ${
            getMetricStatus(metrics?.cls || null, { good: 0.1, poor: 0.25 }) === 'good' 
              ? 'bg-green-100 text-green-800' 
              : getMetricStatus(metrics?.cls || null, { good: 0.1, poor: 0.25 }) === 'needs-improvement'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-medium">CLS</div>
            <div>{formatMetricValue(metrics?.cls || null, '')}</div>
          </div>

          <div className={`p-2 rounded ${
            getMetricStatus(metrics?.fcp || null, { good: 1800, poor: 3000 }) === 'good' 
              ? 'bg-green-100 text-green-800' 
              : getMetricStatus(metrics?.fcp || null, { good: 1800, poor: 3000 }) === 'needs-improvement'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-medium">FCP</div>
            <div>{formatMetricValue(metrics?.fcp || null)}</div>
          </div>
        </div>

        {slowComponents.length > 0 && (
          <div className="mt-3">
            <h4 className="font-medium text-gray-700 mb-2">Slow Components (&gt;{threshold}ms)</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {slowComponents.slice(0, 5).map((comp, index) => (
                <div key={index} className="flex justify-between text-xs bg-red-50 p-1 rounded">
                  <span className="truncate">{comp.componentName}</span>
                  <span className="text-red-600 font-medium">
                    {comp.renderTime.toFixed(1)}ms
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
          Press Ctrl+Shift+P to toggle
        </div>
      </div>
    </div>
  );
};

export default PerformanceMonitor;
